/**
 * LA SU - Product: keeps the cart in step after a partial add.
 * sections/lasu-main-product.liquid · brief docs/sections/lasu-main-product.md §11
 *
 * Asked for more than the stock, Shopify adds what it has and still answers
 * with an error: "Aufgrund der Verfügbarkeit wurden nur 50 Artikel zu deinem
 * Warenkorb hinzugefügt." Dawn's product-form.js reads every error as
 * "nothing added": it shows the message and stops (product-form.js:52-69).
 * So the drawer and the header's count stay stale until a reload. Dawn's own
 * product page behaves the same.
 *
 * On that error, this asks the cart whether the variant's QUANTITY rose: the
 * drawer's rendered quantity before, against /cart.js after. Presence alone
 * isn't enough (2026-09-29 audit): a variant already in the cart made every
 * failed add — stock used up, a B2B quantity rule — open the drawer as if
 * something had been added. If it rose, this does what a successful add
 * does, through the drawer's own method (cart-drawer.js:79-96): it
 * re-renders the drawer and the count, and opens the drawer. Shopify's
 * message goes into the drawer's error line, where cart.js puts its own
 * errors, so the open drawer doesn't hide it. If it didn't, nothing was
 * added, and Dawn's inline message is the whole story.
 *
 * Uses Dawn's pubsub.js and constants.js (loaded in theme.liquid) and its
 * <cart-drawer>. With the cart notification instead of the drawer, it does
 * nothing.
 */

if (!customElements.get('lasu-main-product')) {
  // A failed fetch must land in the catch below, not be parsed as a cart.
  const readJson = (response) => {
    if (!response.ok) throw new Error(`${response.status} ${response.url}`);
    return response.json();
  };

  customElements.define(
    'lasu-main-product',
    class LasuMainProduct extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      onCartError(event) {
        // This section's form only: the product cards on the page publish the
        // same event for their own variants.
        const variantInput = this.querySelector('product-form [name="id"]');
        if (event.source !== 'product-form' || !variantInput) return;
        if (String(event.productVariantId) !== variantInput.value) return;

        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.renderContents !== 'function') return;

        const variantId = String(event.productVariantId);
        const sectionIds = drawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');

        // Before: what the drawer shows now, i.e. the cart before this add. A
        // variant can sit on several lines (selling plans, properties), so
        // every line is summed, on both sides.
        const before = Array.from(drawer.querySelectorAll('.quantity__input[data-quantity-variant-id]'))
          .filter((input) => input.dataset.quantityVariantId === variantId)
          .reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);

        Promise.all([
          fetch(`${routes.cart_url}.js`).then(readJson),
          fetch(`${routes.cart_url}?sections=${sectionIds}`).then(readJson),
        ])
          .then(([cart, sections]) => {
            const after = cart.items
              .filter((item) => String(item.variant_id) === variantId)
              .reduce((sum, item) => sum + item.quantity, 0);
            if (after <= before) return;

            drawer.renderContents({ id: variantId, sections });

            // Set by product-form.js by now: it writes the message right after
            // publishing the event, and this runs after two fetches.
            const message = this.querySelector('.product-form__error-message');
            const drawerErrors = document.getElementById('CartDrawer-CartErrors');
            if (message && drawerErrors) drawerErrors.textContent = message.textContent;
          })
          .catch((error) => console.error(error));
      }
    }
  );
}
